import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { brl } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, CalendarDays, X } from "lucide-react";

const fmt = (d) => (d ? d.split("-").reverse().join("/") : "");

const FichaRow = ({ f, onOpen }) => (
  <button onClick={onOpen} data-testid={`mf-ficha-${f.id}`}
    className="w-full flex items-center justify-between gap-3 px-4 py-3 border-b border-border last:border-b-0 text-left hover:bg-secondary/40 transition-colors">
    <div className="min-w-0 flex-1">
      <p className="font-semibold text-foreground truncate">{f.cliente_nome || "Sem nome"}</p>
      {f.mostrarProduto && <p className="text-xs text-foreground/80 truncate">{f.produtos}</p>}
      <p className="text-xs text-muted-foreground">Ficha nº {f.numero} · {fmt(f.data_compra)} · {f.quantidade} un.</p>
      {f.quitada && f.data_quitacao && <p className="text-xs text-primary">Liquidada em {fmt(f.data_quitacao)}</p>}
    </div>
    <div className="text-right shrink-0">
      <p className="font-display font-bold text-foreground">{brl(f.total_compra)}</p>
      {!f.quitada && <p className="text-xs text-destructive">Falta {brl(f.saldo)}</p>}
    </div>
  </button>
);

export const FichasAbas = ({ fichas, vazioExtra = "", mostrarProduto = false }) => {
  const [tab, setTab] = useState("pagando");
  const navigate = useNavigate();
  const pagando = (fichas || []).filter((f) => !f.quitada);
  const liquidadas = (fichas || []).filter((f) => f.quitada);
  const lista = tab === "pagando" ? pagando : liquidadas;
  return (
    <div className="space-y-3 min-w-0">
      <div className="grid grid-cols-2 gap-2">
        {[["pagando", `Pagando (${pagando.length})`], ["liquidadas", `Liquidadas (${liquidadas.length})`]].map(([v, l]) => (
          <button key={v} data-testid={`mf-tab-${v}`} onClick={() => setTab(v)}
            className={`min-w-0 truncate rounded-full py-2 text-sm font-semibold border-2 transition-colors ${tab === v ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      {!fichas ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        : lista.length === 0 ? <p data-testid="mf-vazio" className="text-center text-muted-foreground py-8">Nenhuma ficha {tab === "pagando" ? "pagando" : "liquidada"}{vazioExtra}.</p>
        : <div className="rounded-xl border border-border bg-card overflow-hidden min-w-0" data-testid="mf-lista">
            {lista.map((f) => <FichaRow key={f.id} f={{ ...f, mostrarProduto }} onOpen={() => navigate(`/painel/ficha/${f.id}`)} />)}
          </div>}
    </div>
  );
};

export const MercadoriaFichas = ({ descricao, onClose, initialDe = "", initialAte = "" }) => {
  const [fichas, setFichas] = useState(null);
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");

  useEffect(() => {
    if (descricao) { setDe(initialDe); setAte(initialAte); }
  }, [descricao, initialDe, initialAte]);

  useEffect(() => {
    if (!descricao) { return; }
    setFichas(null);
    api.get("/dashboard/mais-vendidas/fichas", { params: { descricao, de, ate } })
      .then((r) => setFichas(r.data.fichas))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [descricao, de, ate]);

  return (
    <Dialog open={!!descricao} onOpenChange={(o) => { if (!o) { onClose(); setDe(""); setAte(""); } }}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden rounded-2xl p-4 sm:p-6 [&>*]:min-w-0" onOpenAutoFocus={(e) => e.preventDefault()} data-testid="mf-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl pr-6">{descricao}</DialogTitle></DialogHeader>
        <p className="flex items-center gap-2 text-sm text-muted-foreground -mb-2"><CalendarDays className="w-4 h-4" /> Data da compra</p>
        <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
          <label className="min-w-0 text-xs text-muted-foreground">De
            <Input type="date" data-testid="mf-de" value={de} onChange={(e) => setDe(e.target.value)} className="mt-1 bg-card min-w-0 w-full px-2 text-sm" />
          </label>
          <label className="min-w-0 text-xs text-muted-foreground">Até
            <Input type="date" data-testid="mf-ate" value={ate} onChange={(e) => setAte(e.target.value)} className="mt-1 bg-card min-w-0 w-full px-2 text-sm" />
          </label>
          {(de || ate) ? <button data-testid="mf-limpar-data" onClick={() => { setDe(""); setAte(""); }} className="mb-2 p-1 text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button> : <span />}
        </div>
        <FichasAbas fichas={fichas} vazioExtra={de || ate ? " nesse período" : ""} />
      </DialogContent>
    </Dialog>
  );
};
